import { useTranslations } from '@vocab/react';
import { IconClear, Box, Text } from 'braid-design-system';
import { observer } from 'mobx-react';
import React, {
  useEffect,
  type ReactElement,
  useRef,
  type KeyboardEvent,
  useState,
} from 'react';
import FocusLock from 'react-focus-lock';

import type { MODAL_NAME, OVERLAY_MODAL } from '../../../constants/modals';
import type { IModalStore } from '../../../stores/UiStore/ModalStore';
import { ESCAPE_KEY } from '../../../utils/buildHandleEnterAndSpaceKeyPress';
import useEventListener from '../../../utils/hooks/useEventListener';
import TextButton from '../TextButton';

import translations from './.vocab';

import * as styles from './Modal.css';

interface BaseProps {
  name: MODAL_NAME | OVERLAY_MODAL;
  contentClassName?: string;
  overlayClassName?: string;
  closeButton?: boolean;
  modalStore: IModalStore;
  onCloseModal?: () => void;
  showDraw?: boolean;
}

type CustomModalProps = BaseProps & {
  renderCustomElement: (props: {
    isOpen: boolean;
    close: () => void;
  }) => ReactElement | null;
  children?: never;
};

type ModalWithChildrenProps = BaseProps & {
  children: (props: {
    enableModal: () => void;
    disableModal: () => void;
  }) => ReactElement | null;
  renderCustomElement?: never;
};

type Props = CustomModalProps | ModalWithChildrenProps;

const Modal = ({
  modalStore,
  name,
  contentClassName = '',
  overlayClassName = '',
  closeButton = true,
  onCloseModal,
  showDraw = false,
  ...renderFunctions
}: Props) => {
  const { t } = useTranslations(translations);
  const [isEnabled, setIsEnabled] = useState(true);

  const content = useRef<HTMLDivElement>(null);

  const show = modalStore.modals.indexOf(name) > -1;

  const overlayClassNames = [
    styles.overlay,
    overlayClassName,
    { [styles.overlayDrawer]: showDraw },
  ];

  const contentClassNames = `${styles.contentStyle} ${contentClassName} ${
    showDraw ? styles.drawer : styles.popup
  }`;

  const closeModal = () => {
    if (onCloseModal) {
      onCloseModal();
    }
    modalStore.removeModal(name);
  };

  useEffect(() => {
    if (show) {
      document.querySelector('body')!.classList.add('modal');
    } else if (modalStore.modals.length === 0) {
      document.querySelector('body')!.classList.remove('modal');
    }
  }, [modalStore.modals.length, show]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isEnabled) {
      return;
    }

    if (event.key === ESCAPE_KEY) {
      event.stopPropagation();
      closeModal();
    }
  };

  useEventListener('keyDown', handleKeyDown);

  const clickToClose = (e: { target: any }) => {
    if (!content || !content.current || !isEnabled) {
      return;
    }
    const overlayOnly = !content.current.contains(e.target);

    if (overlayOnly) {
      closeModal();
    }
  };

  const enableModal = () => setIsEnabled(true);
  const disableModal = () => setIsEnabled(false);

  return show ? (
    <FocusLock
      returnFocus
      // Ensure we only lock focus on elements within the react 'app' (root component).
      // This avoids issues with forms in popups like hotjar
      whiteList={(node) =>
        document?.getElementById('app')?.contains(node) ?? false
      }
    >
      {renderFunctions.renderCustomElement &&
        renderFunctions.renderCustomElement({
          isOpen: show,
          close: closeModal,
        })}

      {renderFunctions.children && (
        <Box
          className={overlayClassNames}
          onMouseDown={clickToClose}
          zIndex="modal"
        >
          <div
            ref={content}
            className={contentClassNames}
            onKeyDown={handleKeyDown}
          >
            {closeButton && (
              <Box
                className={styles.header}
                alignItems="center"
                display="flex"
                justifyContent="flexEnd"
              >
                {isEnabled && (
                  <Text>
                    <TextButton
                      aria-label={t('Close modal')}
                      data-testid={`close-modal-${name}`}
                      onClick={closeModal}
                    >
                      <IconClear
                        title={t('Close modal')}
                        titleId="close-modal"
                      />
                    </TextButton>
                  </Text>
                )}
              </Box>
            )}
            <div className={styles.body} tabIndex={-1}>
              {renderFunctions.children({ enableModal, disableModal })}
            </div>
          </div>
        </Box>
      )}
    </FocusLock>
  ) : (
    <></>
  );
};

export default observer(Modal);
